import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "../../../api/axios";
import { useCurrentTenant } from "../../../hooks/useCurrentTenant";
import NewScenarioModal from "../../Scenarios/NewScenarioModal"; // ADAPT: path to your modal
import "./ScenarioSelector.css";

export default function ScenarioSelector({ selectedScenario, onSelectScenario }) {
  const currentTenantId = useCurrentTenant();
  const navigate = useNavigate();

  const [scenarioList, setScenarioList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedId, setSelectedId] = useState(selectedScenario?.id ?? "");
  const [showModal, setShowModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);
  const appliedDefault = useRef(false);

  const loadScenarios = () =>
    api.get("/scenario")
      .then((res) => { setScenarioList(Array.isArray(res.data) ? res.data : []); setLoadError(false); })
      .catch((err) => { console.error("Failed to load scenarios:", err); setLoadError(true); })
      .finally(() => setListLoading(false));

  useEffect(() => { loadScenarios(); }, []);

  // Reload when a scenario changed elsewhere (e.g. default flag set in ScenarioManager)
  useEffect(() => {
    const onUpdated = () => { loadScenarios(); };
    window.addEventListener("scenario-updated", onUpdated);
    return () => window.removeEventListener("scenario-updated", onUpdated);
  }, []);

  const tenantScenarios = useMemo(
    () => scenarioList.filter((s) => s.tenantId === currentTenantId && s.isTemplate !== true),
    [scenarioList, currentTenantId]
  );

  const defaultScenario = tenantScenarios.find((s) => s.isDefault) ?? null;

  // Sync with parent
  useEffect(() => {
    if (!selectedScenario || tenantScenarios.length === 0) return;
    if (tenantScenarios.some((s) => s.id === selectedScenario.id)) setSelectedId(selectedScenario.id);
  }, [selectedScenario?.id, tenantScenarios]); // eslint-disable-line react-hooks/exhaustive-deps

  // Nothing selected yet -> pre-select the default scenario (once)
  useEffect(() => {
    if (appliedDefault.current || selectedScenario || !defaultScenario) return;
    appliedDefault.current = true;
    setSelectedId(defaultScenario.id);
    onSelectScenario?.(defaultScenario);
  }, [defaultScenario, selectedScenario]); // eslint-disable-line react-hooks/exhaustive-deps

  const current = tenantScenarios.find((s) => s.id === selectedId) ?? null;

  const handleSelect = (e) => {
    const id = e.target.value;
    setSelectedId(id);
    onSelectScenario?.(tenantScenarios.find((s) => s.id === id) ?? null);
  };

  // Set / unset the standard scenario in the backend (only one default per tenant)
  const handleToggleStandard = async (e) => {
    if (!current) return;
    const makeDefault = e.target.checked;
    setSavingDefault(true);
    try {
      if (makeDefault) await api.post(`/scenario/${current.id}/default`);
      else await api.delete(`/scenario/${current.id}/default`);

      setScenarioList((list) =>
        list.map((s) =>
          s.id === current.id
            ? { ...s, isDefault: makeDefault }
            : makeDefault && s.tenantId === current.tenantId
              ? { ...s, isDefault: false }
              : s
        )
      );
    } catch (err) {
      console.error("Failed to change default scenario:", err);
      alert("Default scenario could not be changed.");
    } finally {
      setSavingDefault(false);
    }
  };

  const handleCreateScenario = async ({ name, description }) => {
    setCreating(true);
    try {
      const { data: scenario } = await api.post("/scenario", {
        name,
        tenantId: "",
        description,
        settings: {
          tolerance: 0,
          fillMountingSpace: 200,
          minDistanceWireDuct: 10,
          orientation: 0,
          connection: "",
          placementOptions: {
            enableSubCabinetFill: true,
            bandSeparatorCableDuctWidthMm: 40,
            requireCurrentRowGEQNextFirstRow: false,
            emitBandSeparatorCableDuct: true,
          },
        },
        sections: [],
        sequences: [],
        subGroupSequenceGroups: [],
      });
      navigate(`/scenarios/${scenario.id}`);
    } catch (err) {
      console.error("Failed to create scenario:", err);
      alert("Scenario could not be created.");
      setCreating(false);
    }
  };

  return (
    <div className="ss-root">
      <select
        className="pl-input ss-select"
        value={selectedId}
        onChange={handleSelect}
        disabled={listLoading || loadError}
        aria-label="Scenario"
      >
        {!current && <option value="">{listLoading ? "Loading scenarios…" : "-- Select a Scenario --"}</option>}
        {tenantScenarios.map((sc) => (
          <option key={sc.id} value={sc.id}>{sc.name}{sc.isDefault ? " (default)" : ""}</option>
        ))}
      </select>

      {loadError && <p className="pl-empty ss-error">Failed to load scenarios.</p>}
      {!listLoading && !loadError && tenantScenarios.length === 0 && (
        <p className="pl-empty ss-error">No scenarios yet. Create your first one below.</p>
      )}
{/* 
      <label className={`ss-check ${current && !savingDefault ? "" : "disabled"}`}>
        <input
          type="checkbox"
          checked={!!current?.isDefault}
          disabled={!current || savingDefault}
          onChange={handleToggleStandard}
        />
        <span>set this as standard scenario</span>
      </label> */}

      <textarea
        className="pl-input ss-description"
        readOnly
        value={current?.description ?? ""}
        placeholder={current ? "No description" : "Select a scenario to see its description"}
        aria-label="Scenario description"
      />
{/* 
      <div className="ss-links">
        <button type="button" className="ss-link" disabled={creating} onClick={() => setShowModal(true)}>
          Create or edit scenario
        </button>
        {current && (
          <button type="button" className="ss-link ss-link-secondary" onClick={() => navigate(`/scenarios/${current.id}`)}>
            Edit "{current.name}"
          </button>
        )}
      </div> */}

      {showModal &&
        createPortal(
          <NewScenarioModal onCreate={handleCreateScenario} onClose={() => setShowModal(false)} />,
          document.body
        )}
    </div>
  );
}